import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS }                 from '../typings/chart';
import { ChartPointModel }              from '../typings/intelligence.chart-point';
import { ChartSeriesModel }             from '../typings/intelligence.chart-series';
import { IntelligenceFormatService }    from '../intelligence.format.service';
import { IntelligenceMechanicJobModel } from '../typings/intelligence.mechanic-job';
import { IntelligenceMechanicModel }    from '../typings/intelligence.mechanic';
import { IntelligenceMechanicsModel }   from '../typings/intelligence.mechanics';
import { IntelligenceService }          from '../intelligence.service';
import { TableSortModel }               from '../typings/intelligence.table-sort';

@Component({
  selector   : 'wor-intelligence-mechanics-tab',
  template: require('./intelligence.mechanics-tab.component.html')
})
export class MechanicsTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  elrValue = '';
  jobs : Array<IntelligenceMechanicJobModel> = [];
  jobsLoading = false;
  jobsSort : TableSortModel = { direction: 'desc', field: 'post_date' };
  mechanics : Array<IntelligenceMechanicModel> = [];
  performancePoints : Array<ChartPointModel> = [];
  selectedMechanic : IntelligenceMechanicModel = null;
  sort : TableSortModel = { direction: 'desc', field: 'labour_revenue_ex_tax' };
  trendPoints : Array<ChartPointModel> = [];

  performanceSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') },
    { color: CHART_COLORS[ 0 ], key: 'cost', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.COST') }
  ];

  trendSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'revenue', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.REVENUE') }
  ];

  private jobsGeneration = 0;
  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    public intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService      : IntelligenceService,
    private translateService        : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  elr ( mechanic : IntelligenceMechanicModel ) : string {
    return mechanic.effective_labour_rate === null || mechanic.effective_labour_rate === undefined
      ? '—'
      : `${ this.intelligenceFormatService.money(mechanic.effective_labour_rate) }/h`;
  }

  jobsCsvRows () : Array<any> {
    return this.jobs.map(job => ({
      post_date      : job.post_date,
      plate_number   : job.plate_number,
      job_card_number: job.job_card_number,
      hours_worked   : job.hours_worked,
      hours_charged  : job.hours_charged,
      amount_charged : job.amount_charged_ex_tax,
      cost           : job.cost_ex_tax,
      gross_profit   : job.gross_profit_ex_tax,
      profit_per_hour: job.profit_per_hour,
      efficiency_pct : job.efficiency_pct
    }));
  }

  profitPerHour ( job : IntelligenceMechanicJobModel ) : string {
    return job.profit_per_hour === null || job.profit_per_hour === undefined
      ? '—'
      : `${ this.intelligenceFormatService.money(job.profit_per_hour) }/h`;
  }

  onMechanicChange ( mechanicId : string ) : void {
    const mechanic = this.mechanics.find(candidate => candidate.mechanic_id === mechanicId);

    if ( mechanic ) {
      this.selectMechanic(mechanic);
    }
  }

  selectMechanic ( mechanic : IntelligenceMechanicModel ) : void {
    this.selectedMechanic = mechanic;

    this.loadJobs();
  }

  sortJobsBy ( field : string ) : void {
    this.jobsSort = {
      direction: this.jobsSort.field === field && this.jobsSort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.jobs = this.intelligenceFormatService.sortRows(this.jobs, this.jobsSort.field, this.jobsSort.direction);
  }

  isEmpty () : boolean {
    return !this.mechanics.length;
  }

  isUnderRecovered ( mechanic : IntelligenceMechanicModel ) : boolean {
    return mechanic.recovery_rate !== null
      && mechanic.recovery_rate !== undefined
      && mechanic.recovery_rate < 100;
  }

  load () : void {
    const generation = ++this.loadGeneration;

    ++this.jobsGeneration;

    this.error = false;
    this.jobsLoading = false;
    this.loading = true;

    this.intelligenceService.getMechanics()
    .then(( data : IntelligenceMechanicsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.mechanics = this.intelligenceFormatService.sortRows(data.mechanics || [], this.sort.field, this.sort.direction);

      this.performancePoints = this.mechanics.map(mechanic => ({
        label : mechanic.name,
        values: {
          cost   : mechanic.labour_cost_ex_tax,
          revenue: mechanic.labour_revenue_ex_tax
        }
      }));

      this.trendPoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: { revenue: day.labour_revenue_ex_tax }
      }));

      this.elrValue = data.totals && data.totals.effective_labour_rate !== null && data.totals.effective_labour_rate !== undefined
        ? `${ this.intelligenceFormatService.money(data.totals.effective_labour_rate) }/h`
        : '—';

      const current = this.selectedMechanic
        ? this.mechanics.find(mechanic => mechanic.mechanic_id === this.selectedMechanic.mechanic_id)
        : null;

      this.selectedMechanic = current || this.mechanics[ 0 ] || null;
      this.loadJobs();
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  private loadJobs () : void {
    if ( !this.selectedMechanic ) {
      return;
    }

    const generation = ++this.jobsGeneration;

    this.jobsLoading = true;

    this.intelligenceService.getMechanicJobs(this.selectedMechanic.mechanic_id)
    .then(data => {
      if ( generation !== this.jobsGeneration ) {
        return;
      }

      this.jobs = this.intelligenceFormatService.sortRows(data.jobs || [], this.jobsSort.field, this.jobsSort.direction);
    })
    .catch(() => {
      if ( generation === this.jobsGeneration ) {
        this.jobs = [];
      }
    })
    .finally(() => {
      if ( generation === this.jobsGeneration ) {
        this.jobsLoading = false;
      }
    });
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.mechanics = this.intelligenceFormatService.sortRows(this.mechanics, this.sort.field, this.sort.direction);
  }
}
